import {
  TenantEnvironmentStage,
  TenantModuleFlags,
  TenantQuickStartConfig,
} from '../config/tenant';

export interface QuickStartGuardEvaluation {
  allowed: boolean;
  reason?: 'demo_sensitive_module' | 'missing_operational_checklist';
  details?: string;
  missingChecklist?: string[];
}

const intersects = (
  a: Array<keyof TenantModuleFlags>,
  b: Array<keyof TenantModuleFlags>
): boolean => a.some((item) => b.includes(item));

export const evaluateQuickStartModuleGuards = (input: {
  environmentStage: TenantEnvironmentStage;
  requiredModules?: Array<keyof TenantModuleFlags>;
  quickStart: TenantQuickStartConfig;
}): QuickStartGuardEvaluation => {
  const requiredModules = input.requiredModules || [];
  if (requiredModules.length === 0) {
    return { allowed: true };
  }

  const configGuards = input.quickStart.configGuards;
  if (!configGuards) {
    return { allowed: true };
  }

  const sensitiveModules = configGuards.sensitiveModules || [];
  if (
    configGuards.blockSensitiveModulesInDemo &&
    input.environmentStage === 'demo' &&
    intersects(requiredModules, sensitiveModules)
  ) {
    return {
      allowed: false,
      reason: 'demo_sensitive_module',
      details: 'Sensitive modules are blocked while tenant stage is Demo.',
    };
  }

  if (!configGuards.enforceOperationalChecklist) {
    return { allowed: true };
  }

  const checklist = input.quickStart.operationalChecklist || [];
  const requiredChecklist = checklist.filter((item) => {
    const requiredForModules = item.requiredForModules || [];
    return requiredForModules.length > 0 && intersects(requiredModules, requiredForModules);
  });

  const missingChecklist = requiredChecklist
    .filter((item) => !item.completed)
    .map((item) => item.title);

  if (missingChecklist.length > 0) {
    return {
      allowed: false,
      reason: 'missing_operational_checklist',
      details: 'Required operational checklist items are not complete.',
      missingChecklist,
    };
  }

  return { allowed: true };
};
